// Model prices (was "Price book", SCR-005). Everyone with `billing:read` reads it; only a
// super-admin sees Add price and Delete (the server enforces this — ADR-031 — the screen just
// doesn't show what can't be used). The book is answered whole, so the filters apply locally.
import { useContext, useEffect, useState } from "preact/hooks";
import { useApi } from "@nexus/ui";
import { ApiError } from "@nexus/ui";
import { SessionContext } from "../../session/session";
import { Dialog } from "@nexus/ui";
import { Checkbox, Field, Fieldset, InputAffix, errorId, hintId } from "@nexus/ui";
import { fieldErrorsFrom } from "@nexus/ui";
import { Icon } from "@nexus/ui";
import { Chip, EmptyState, FilterBar, PageHeader } from "@nexus/ui";
import { ErrorDetails } from "@nexus/ui";
import { DataTable, Money, type Column } from "@nexus/ui";
import { formatDay } from "../billing/model";
import {
  EMPTY_ADD_PRICE_FORM,
  EMPTY_FILTERS,
  addPriceRequestFrom,
  filterPriceRows,
  modelCount,
  needsBackdate,
  priceRows,
  validateAddPriceForm,
  type AddPriceFormValues,
  type ModelPrice,
  type PriceFilters,
  type PriceRowView,
} from "./model";

interface FetchState {
  loading: boolean;
  rows: ModelPrice[];
  error: ApiError | null;
}

type RateField = "inputPerM" | "outputPerM" | "cacheReadPerM" | "cacheWritePerM" | "cacheWrite1hPerM";

const RATE_FIELDS: readonly { field: RateField; label: string; help: "prices.input" | "prices.output" | "prices.cacheRead" | "prices.cacheWrite" | "prices.cacheWrite1h"; hint?: string }[] = [
  { field: "inputPerM", label: "Input", help: "prices.input" },
  { field: "outputPerM", label: "Output", help: "prices.output" },
  { field: "cacheReadPerM", label: "Cache read", help: "prices.cacheRead" },
  { field: "cacheWritePerM", label: "Cache write (5 min)", help: "prices.cacheWrite" },
  { field: "cacheWrite1hPerM", label: "Cache write (1 hour)", help: "prices.cacheWrite1h", hint: "Empty: same as a 5-minute write." },
];

function ApiErrorBanner({ error }: { error: ApiError }) {
  return (
    <div class="banner banner-error" role="alert">
      <p>
        <code class="error-code">{error.code}</code> {error.message}
      </p>
      <ErrorDetails error={error} />
    </div>
  );
}

function AddPriceDialog({ open, onClose, onAdded }: { open: boolean; onClose(): void; onAdded(): void }) {
  const client = useApi();
  const [values, setValues] = useState<AddPriceFormValues>(EMPTY_ADD_PRICE_FORM);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const set = <K extends keyof AddPriceFormValues>(key: K, value: AddPriceFormValues[K]) =>
    setValues((current) => ({ ...current, [key]: value }));

  const clientErrors = validateAddPriceForm(values);
  const serverErrors = error ? fieldErrorsFrom(error) : {};
  // Client-side problems show once the user has tried to add (a fresh form is not all red).
  const errors = { ...(submitted ? clientErrors : {}), ...serverErrors };
  const backdate = needsBackdate(values.validFrom, new Date());

  const submit = async () => {
    setSubmitted(true);
    if (Object.keys(clientErrors).length > 0) return;
    setBusy(true);
    setError(null);
    try {
      const { body, backdate: backdated } = addPriceRequestFrom(values, new Date());
      await client.post("/v1/admin/prices", { body, ...(backdated ? { query: { backdate: true } } : {}) });
      setValues(EMPTY_ADD_PRICE_FORM);
      setSubmitted(false);
      onAdded();
      onClose();
    } catch (err) {
      if (err instanceof ApiError) setError(err);
      else throw err;
    } finally {
      setBusy(false);
    }
  };

  const describe = (id: string, message: string | undefined, hint = false) =>
    [hint ? hintId(id) : null, message ? errorId(id) : null].filter(Boolean).join(" ") || undefined;

  return (
    <Dialog
      title="Add price"
      open={open}
      onClose={onClose}
      class="dialog-wide"
      actions={
        <>
          <button type="button" class="button button-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="button" class="button button-primary" disabled={busy} onClick={() => void submit()}>
            {busy ? "Adding…" : "Add price"}
          </button>
        </>
      }
    >
      <div class="stack">
        <p class="dialog-message">
          A new dated AWS list price for one model. Usage from the valid-from day on is priced at it; months already closed never change.
        </p>
        {error && <ApiErrorBanner error={error} />}
        <div class="form-grid price-form-top">
          <Field id="price-model" label="Bedrock model id" help="prices.bedrockModelId" error={errors.bedrockModelId}>
            <input
              id="price-model"
              class="input-mono"
              value={values.bedrockModelId}
              placeholder="anthropic.claude-sonnet-4-5-20250929-v1:0"
              aria-invalid={Boolean(errors.bedrockModelId)}
              aria-describedby={describe("price-model", errors.bedrockModelId)}
              onInput={(e) => set("bedrockModelId", e.currentTarget.value)}
            />
          </Field>
          <Field
            id="price-valid-from"
            label="Valid from"
            help="prices.validFrom"
            error={errors.validFrom}
            hint={backdate ? "Today or earlier: re-prices open months' history (no backfill); sent as a backdated price." : "Empty: tomorrow (UTC)."}
          >
            <input
              id="price-valid-from"
              type="date"
              value={values.validFrom}
              aria-invalid={Boolean(errors.validFrom)}
              aria-describedby={describe("price-valid-from", errors.validFrom, true)}
              onInput={(e) => set("validFrom", e.currentTarget.value)}
            />
          </Field>
        </div>
        <Fieldset label="Dollars per million tokens" help="prices.rates" class="price-rates">
          <div class="form-grid">
            {RATE_FIELDS.map(({ field, label, help, hint }) => (
              <Field key={field} id={`price-${field}`} label={label} help={help} error={errors[field]} hint={hint}>
                <InputAffix prefix="$">
                  <input
                    id={`price-${field}`}
                    inputMode="decimal"
                    value={values[field]}
                    aria-invalid={Boolean(errors[field])}
                    aria-describedby={describe(`price-${field}`, errors[field], hint !== undefined)}
                    onInput={(e) => set(field, e.currentTarget.value)}
                  />
                </InputAffix>
              </Field>
            ))}
          </div>
        </Fieldset>
        <div class="form-grid">
          <Field id="price-source" label="Source" help="prices.source" error={errors.source}>
            <input
              id="price-source"
              value={values.source}
              aria-invalid={Boolean(errors.source)}
              aria-describedby={describe("price-source", errors.source)}
              onInput={(e) => set("source", e.currentTarget.value)}
            />
          </Field>
          <Field id="price-source-url" label="Source URL" help="prices.sourceUrl" error={errors.sourceUrl}>
            <input
              id="price-source-url"
              type="url"
              value={values.sourceUrl}
              aria-invalid={Boolean(errors.sourceUrl)}
              aria-describedby={describe("price-source-url", errors.sourceUrl)}
              onInput={(e) => set("sourceUrl", e.currentTarget.value)}
            />
          </Field>
          <Field id="price-retrieved-on" label="Retrieved on" help="prices.retrievedOn" error={errors.retrievedOn}>
            <input
              id="price-retrieved-on"
              type="date"
              value={values.retrievedOn}
              aria-invalid={Boolean(errors.retrievedOn)}
              aria-describedby={describe("price-retrieved-on", errors.retrievedOn)}
              onInput={(e) => set("retrievedOn", e.currentTarget.value)}
            />
          </Field>
        </div>
        <Checkbox
          id="price-verified"
          label="Verified against the AWS Price List"
          labelText="Verified"
          help="prices.verified"
          checked={values.verified}
          onChange={(checked) => set("verified", checked)}
        />
      </div>
    </Dialog>
  );
}

function DeletePriceDialog({ row, onClose, onDeleted }: { row: ModelPrice | null; onClose(): void; onDeleted(): void }) {
  const client = useApi();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => setError(null), [row]);

  const remove = async () => {
    if (!row) return;
    setBusy(true);
    setError(null);
    try {
      await client.delete("/v1/admin/prices/{bedrockModelId}/{validFrom}", { path: { bedrockModelId: row.bedrockModelId, validFrom: row.validFrom } });
      onDeleted();
      onClose();
    } catch (err) {
      if (err instanceof ApiError) setError(err);
      else throw err;
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      title="Delete price"
      open={row !== null}
      onClose={onClose}
      actions={
        <>
          <button type="button" class="button button-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="button" class="button button-danger" disabled={busy} onClick={() => void remove()}>
            {busy ? "Deleting…" : "Delete price"}
          </button>
        </>
      }
    >
      {row && (
        <div class="stack-sm">
          <p class="dialog-message">
            Delete the price of <span class="mono">{row.bedrockModelId}</span> valid from {formatDay(row.validFrom)}? Usage on those days is then
            priced at the model's earlier price, or left unpriced if there is none. A price a closed month was invoiced at cannot be deleted.
          </p>
          {error && <ApiErrorBanner error={error} />}
        </div>
      )}
    </Dialog>
  );
}

export function Prices() {
  const client = useApi();
  const services = useContext(SessionContext);
  const superAdmin = services !== null && services.state.status === "signed-in" && services.state.session.allows("super-admin");

  const [filters, setFilters] = useState<PriceFilters>(EMPTY_FILTERS);
  const [state, setState] = useState<FetchState>({ loading: true, rows: [], error: null });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState<ModelPrice | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setState((current) => ({ ...current, loading: true, error: null }));
    client
      .get("/v1/admin/prices", {})
      .then((page) => {
        if (!cancelled) setState({ loading: false, rows: page.items, error: null });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        if (error instanceof ApiError) setState({ loading: false, rows: [], error });
        else throw error;
      });
    return () => {
      cancelled = true;
    };
  }, [reloadToken, client]);

  const reload = () => setReloadToken((token) => token + 1);

  const columns: Column<PriceRowView>[] = [
    {
      key: "model",
      header: "Model",
      cell: (r) => <span class="mono price-model">{r.row.bedrockModelId}</span>,
      compare: (a, b) => a.row.bedrockModelId.localeCompare(b.row.bedrockModelId) || a.row.validFrom.localeCompare(b.row.validFrom),
    },
    {
      key: "valid",
      header: "Valid",
      help: "prices.valid",
      cell: (r) => (
        <span class="stack-cell price-valid">
          <span>From {formatDay(r.row.validFrom)}</span>
          {r.validTo === null ? (
            <span>
              <Chip tone="green">Current</Chip>
            </span>
          ) : (
            <span class="cell-sub">to {formatDay(r.validTo)}</span>
          )}
        </span>
      ),
      compare: (a, b) => a.row.validFrom.localeCompare(b.row.validFrom),
    },
    { key: "input", header: "Input", help: "prices.input", numeric: true, cell: (r) => <Money micro={r.row.inputPerM} /> },
    { key: "output", header: "Output", help: "prices.output", numeric: true, cell: (r) => <Money micro={r.row.outputPerM} /> },
    { key: "cacheRead", header: "Cache read", help: "prices.cacheRead", numeric: true, cell: (r) => <Money micro={r.row.cacheReadPerM} /> },
    { key: "cacheWrite5m", header: "Cache write 5 min", help: "prices.cacheWrite", numeric: true, cell: (r) => <Money micro={r.row.cacheWritePerM} /> },
    {
      key: "cacheWrite1h",
      header: "Cache write 1 hour",
      help: "prices.cacheWrite1h",
      numeric: true,
      cell: (r) => (r.row.cacheWrite1hPerM === null ? <span class="muted">as 5 min</span> : <Money micro={r.row.cacheWrite1hPerM} />),
    },
    {
      key: "source",
      header: "Source",
      help: "prices.source",
      cell: (r) => (
        <span class="stack-cell price-source">
          <span>{r.unverified ? <Chip tone="amber">Unverified</Chip> : <Chip tone="green">Verified</Chip>}</span>
          {r.sourceHref ? (
            <a href={r.sourceHref} target="_blank" rel="noreferrer" class="price-source-text" title={r.row.source}>
              {r.row.source}
              <span class="visually-hidden"> (opens in a new tab)</span>
            </a>
          ) : (
            <span class="price-source-text" title={r.row.source}>
              {r.row.source}
            </span>
          )}
          {r.row.retrievedOn && <span class="cell-sub">Retrieved {formatDay(r.row.retrievedOn)}</span>}
        </span>
      ),
    },
    ...(superAdmin
      ? [
          {
            key: "actions",
            header: "Actions",
            cell: (r: PriceRowView) => (
              <button
                type="button"
                class="button button-sm button-ghost"
                aria-label={`Delete ${r.row.bedrockModelId} from ${r.row.validFrom}`}
                onClick={() => setDeleting(r.row)}
              >
                Delete
              </button>
            ),
          } as Column<PriceRowView>,
        ]
      : []),
  ];

  const all = priceRows(state.rows);
  const shown = filterPriceRows(all, filters);
  const filtered = filters.model.trim() !== "" || filters.effectiveOn.trim() !== "";
  const addButton = superAdmin ? (
    <button type="button" class="button button-primary" onClick={() => setDialogOpen(true)}>
      <Icon name="plus" small />
      Add price
    </button>
  ) : undefined;

  return (
    <div class="page">
      <PageHeader
        title="Model prices"
        lede="The AWS list price of each Bedrock model, by date. Usage is priced at the price valid on the day."
        actions={addButton}
      />
      <FilterBar
        end={
          !state.loading && state.error === null && all.length > 0 ? (
            <span class="muted">
              {shown.length.toLocaleString("en-US")} prices · {modelCount(shown).toLocaleString("en-US")} models · $ per million tokens
            </span>
          ) : undefined
        }
      >
        <Field id="prices-search" label="Model" help="prices.search" compact class="filter">
          <span class="search-input">
            <Icon name="search" small />
            <input
              id="prices-search"
              type="search"
              value={filters.model}
              placeholder="Search model ids"
              onInput={(e) => setFilters({ ...filters, model: e.currentTarget.value })}
            />
          </span>
        </Field>
        <Field id="prices-valid-on" label="Valid on" help="prices.validOn" compact class="filter">
          <input
            id="prices-valid-on"
            type="date"
            class="input"
            value={filters.effectiveOn}
            onInput={(e) => setFilters({ ...filters, effectiveOn: e.currentTarget.value })}
          />
        </Field>
        {filtered && (
          <button type="button" class="button button-ghost button-sm" onClick={() => setFilters(EMPTY_FILTERS)}>
            Clear filters
          </button>
        )}
      </FilterBar>

      {!state.loading && state.error === null && all.length === 0 ? (
        <EmptyState
          title="No model prices yet"
          lede="Usage is priced at the AWS list price valid on the day. Until a model has a price, its usage stays unpriced — never given a default — and a month using it cannot close without a reason."
          steps={[
            { title: "Seed the verified prices", text: "Run migrate up --seed-prices on the API; it adds the AWS Price List rates Nexus ships with." },
            { title: "Add what is missing", text: "Add a dated price for any other model your customers use." },
          ]}
          actions={addButton}
        />
      ) : (
        <div class="card table-card">
          <DataTable
            caption="Model prices"
            hideCaption
            columns={columns}
            rows={shown}
            rowKey={(r) => `${r.row.bedrockModelId}@${r.row.validFrom}`}
            loading={state.loading}
            error={state.error}
            onRetry={reload}
            emptyMessage="No prices match these filters."
            emptyAction={
              <button type="button" class="button" onClick={() => setFilters(EMPTY_FILTERS)}>
                Clear filters
              </button>
            }
          />
        </div>
      )}
      {superAdmin && <AddPriceDialog open={dialogOpen} onClose={() => setDialogOpen(false)} onAdded={reload} />}
      {superAdmin && <DeletePriceDialog row={deleting} onClose={() => setDeleting(null)} onDeleted={reload} />}
    </div>
  );
}
